import Button from 'components/Button/Button';
import { MIN_CHARACTERS_IN_ASSET_NAME } from 'config/constants';
import React, { useContext, useEffect, useState } from 'react';
import { useForm, useFormState, Controller } from 'react-hook-form';
import { getUUID, windowBeforeUnloadEventHandler } from 'utils/utils';
import { IFormInputs } from 'types/formInterface';
import { AssetManagementFormContainer, ButtonsContainer } from '../components/Styled';
import ConfirmCancelModal from '../components/ConfirmCancelModal';
import useAssetManagementHooks from '../hooks/useAssetManagementHooks';
import FormHeader from '../components/FormHeader';
import AddAssetForm from '../forms/AddAssetForm';
import { AssetManagementContext } from 'contexts/assetManagement.context';

export default function FormController() {
  const defaultValues = {
    assetId: getUUID(), // create assetID clientside.
    assetName: '',
    assetSubType: {
      value: '',
      label: '',
    },
    assetOwner: {
      value: '',
      label: '',
    },
    description: '',
  };

  const [formFieldsValid, setFormFieldsValid] = useState(false);

  const {
    register,
    formState: { errors },
    control,
    handleSubmit,
    clearErrors,
    setError,
    watch,
    getValues,
  } = useForm<IFormInputs>({
    defaultValues: defaultValues,
  });
  const { dirtyFields } = useFormState({ control });
  const {
    formSubmitHandler,
    formSubmitted,
    apiCallInProgress,
    handleCancel,
    handleCloseModal,
    handleCloseModalAndSave,
    setFormSubmitted,
  } = useAssetManagementHooks(handleSubmit, formFieldsValid, setError, getValues);

  const {
    assetFormHasChanges,
    setAssetFormHasChanges,
    showCancelConfirmationDialog,
    setShowCancelConfirmationDialog,
  } = useContext(AssetManagementContext);

  useEffect(() => {
    // watch all formFields, and derive if form has changes, and if fields are valid on input changes.
    watch(() => {
      const watchFields: any = watch([
        'assetId',
        'assetName',
        'assetSubType',
        'assetOwner',
        'description',
      ]);

      const assetIDfield = watchFields[0];
      const assetNameField = watchFields[1];
      const assetNameHasRequiredLength = assetNameField.length >= MIN_CHARACTERS_IN_ASSET_NAME;
      const assetIDfieldHasRequiredLength = assetIDfield.length >= 3;
      const assetSubTypeHasValue = watchFields[2].value !== '';
      const assetOwnerHasValue = watchFields[3]?.value !== '';
      const fieldsValid =
        assetNameHasRequiredLength &&
        assetIDfieldHasRequiredLength &&
        assetSubTypeHasValue &&
        assetOwnerHasValue;
      const formIsDirty = dirtyFields && Object.keys(dirtyFields).length > 0;

      setAssetFormHasChanges(formIsDirty);
      setFormSubmitted(false);
      setFormFieldsValid(fieldsValid && formIsDirty);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  useEffect(() => {
    // attach window unload listener to show browser dialog if edit or add assetform has changes.
    if (assetFormHasChanges) {
      window.addEventListener('beforeunload', windowBeforeUnloadEventHandler, {
        capture: true,
      });

      return () => {
        window.removeEventListener('beforeunload', windowBeforeUnloadEventHandler, {
          capture: true,
        });
      };
    }
  }, [assetFormHasChanges]);

  useEffect(() => {
    // ensure dialog is not shown when page loads.
    // set assetformchanges to false on unmount to ensure navigation logic is contained to this page.
    setShowCancelConfirmationDialog(false);
    return () => {
      setAssetFormHasChanges(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AssetManagementFormContainer>
      <FormHeader text="ADD INDIVIDUAL ASSET" />
      <div className="content">
        <AddAssetForm
          register={register}
          errors={errors}
          control={control}
          Controller={Controller}
          formSubmitted={formSubmitted}
          clearErrors={clearErrors}
          defaultValues={defaultValues}
        />
      </div>
      {/* buttons ------------> */}
      <ButtonsContainer>
        <Button
          style={{ width: '170px', height: '50px' }}
          outline
          onClick={handleCancel}
          id="cancelEditAsset"
          className="biggerButton"
        >
          Cancel
        </Button>
        <Button
          style={{ width: '170px', height: '50px' }}
          disabled={!formFieldsValid}
          id="saveEditAsset"
          className="biggerButton"
          onClick={formSubmitHandler}
          isLoading={apiCallInProgress}
        >
          {'Add'}
        </Button>
      </ButtonsContainer>

      {showCancelConfirmationDialog && (
        <ConfirmCancelModal
          xClick={() => setShowCancelConfirmationDialog(false)}
          closeModal={handleCloseModal}
          actionFunction={handleCloseModalAndSave}
        />
      )}
    </AssetManagementFormContainer>
  );
}
